// src/components/dashboard/DossiersClotures.js
import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Calendar, User, RefreshCw, FolderOpen } from "lucide-react";

// Fonction pour calculer le pourcentage de progression
const calculateProgress = (phases = []) => {
  if (phases.length === 0) return 0;
  const completed = phases.filter(
    (phase) => phase.status.toLowerCase() === "terminé"
  ).length;
  return Math.round((completed / phases.length) * 100);
};

export default function DossiersClotures() {
  const [dossiers, setDossiers] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const navigate = useNavigate();

  const fetchDossiersCloturs = async () => {
    try {
      setLoading(true);

      // Récupérer les dossiers clôturés et les utilisateurs en parallèle
      const [dossiersSnap, usersSnap] = await Promise.all([
        getDocs(query(
          collection(db, "dossiers"), 
          where("status", "==", "closed")
        )),
        getDocs(collection(db, "users"))
      ]);

      // Créer la map des utilisateurs
      const users = {};
      usersSnap.forEach(doc => {
        users[doc.id] = doc.data();
      });

      // Traiter les dossiers clôturés
      const dossiersData = dossiersSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Recalculer les progressions si nécessaire
          progressChargeAffaire: data.progressChargeAffaire || (data.phasesChargeAffaire ? calculateProgress(data.phasesChargeAffaire) : 0),
          progressChefProjet: data.progressChefProjet || (data.phasesChefProjet ? calculateProgress(data.phasesChefProjet) : 0),
        };
      });

      // Calculer la progression globale si manquante
      dossiersData.forEach(dossier => {
        if (!dossier.progressGlobal) {
          dossier.progressGlobal = Math.round((dossier.progressChargeAffaire + dossier.progressChefProjet) / 2);
        }
      });

      // Trier par date de clôture (plus récents en premier)
      dossiersData.sort((a, b) => {
        const dateA = a.closedAt?.toDate() || new Date(0);
        const dateB = b.closedAt?.toDate() || new Date(0);
        return dateB - dateA;
      });

      setUsersMap(users);
      setDossiers(dossiersData);

    } catch (error) {
      console.error("Erreur lors du chargement des dossiers clôturés:", error);
      alert("Erreur lors du chargement des dossiers clôturés");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour restaurer un dossier (le remettre en état actif)
  const handleRestoreDossier = async (dossierId, dossierTitle) => {
    const confirmMessage = `Êtes-vous sûr de vouloir restaurer ce dossier ?\n\n"${dossierTitle}"\n\nLe dossier sera remis dans la liste principale des dossiers actifs.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setRestoring(dossierId);
      const dossierRef = doc(db, "dossiers", dossierId);
      
      await updateDoc(dossierRef, {
        status: 'en cours', // ou un autre statut approprié
        closed: false,
        restoredAt: serverTimestamp(),
        restoredBy: 'admin' // Vous pouvez ajouter l'ID de l'utilisateur ici si nécessaire
      });

      alert("Dossier restauré avec succès.");
      
      // Rafraîchir la liste
      fetchDossiersCloturs();
      
    } catch (error) {
      console.error("Erreur lors de la restauration du dossier:", error);
      alert("Une erreur est survenue lors de la restauration du dossier.");
    } finally {
      setRestoring(null);
    }
  };

  useEffect(() => {
    fetchDossiersCloturs();
  }, []);

  const getFullName = (id) => {
    const user = usersMap[id];
    return user ? `${user.firstName} ${user.lastName}` : "Non défini";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Non définie";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Dossiers Clôturés ({dossiers.length})
            </h1>
            <p className="text-sm text-gray-600">
              Dossiers terminés et archivés
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchDossiersCloturs}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </button>
          <button
            onClick={() => navigate('/admin/dossiers')}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Dossiers actifs
          </button>
        </div>
      </div>

      {/* Liste des dossiers clôturés */}
      {dossiers.length === 0 ? (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
            <div>
              <p className="text-green-700 font-medium">Aucun dossier clôturé</p>
              <p className="text-green-600 text-sm">
                Tous les dossiers sont encore actifs ou en cours de traitement.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {dossiers.map(dossier => (
            <div key={dossier.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6">
                {/* En-tête du dossier avec badge clôturé */}
                <div className="flex justify-between items-start mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">{dossier.title}</h2>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✅ Clôturé
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-3">{dossier.description}</p>

                {/* Informations du dossier */}
                <div className="space-y-2 mb-4">
                  <p className="text-sm"><strong>Client :</strong> {dossier.clientName || "Non défini"}</p>
                  <p className="text-sm"><strong>Email :</strong> {dossier.clientEmail || "Non défini"}</p>
                  <p className="text-sm"><strong>Type :</strong> {dossier.type}</p>
                  <p className="text-sm"><strong>Chef de Projet :</strong> {getFullName(dossier.chefProjet)}</p>
                  <p className="text-sm"><strong>Chargé(e) d'affaire :</strong> {getFullName(dossier.chargeAffaire)}</p>
                </div>

                {/* Barres de progression */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Progression finale</span>
                    <span className="text-xs font-medium text-gray-900">{dossier.progressGlobal || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${dossier.progressGlobal || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Informations de clôture */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="font-medium">Clôturé le :</span>
                    <span className="ml-2">{formatDate(dossier.closedAt)}</span>
                  </div>
                  {dossier.closedBy && (
                    <div className="flex items-center text-sm text-gray-600 mt-1">
                      <User className="w-4 h-4 mr-2" />
                      <span className="font-medium">Par :</span>
                      <span className="ml-2">{dossier.closedBy}</span>
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/admin/dossier/${dossier.id}`)}
                    className="flex-1 text-blue-600 hover:underline text-sm"
                  >
                    Voir les détails
                  </button>
                  
                  <button
                    onClick={() => handleRestoreDossier(dossier.id, dossier.title)}
                    disabled={restoring === dossier.id}
                    className="flex items-center justify-center px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {restoring === dossier.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1"></div>
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    {restoring === dossier.id ? 'Restauration...' : '🔄 Restaurer'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Statistiques en bas */}
      {dossiers.length > 0 && (
        <div className="mt-8 bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Statistiques des Dossiers Clôturés</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{dossiers.length}</p>
              <p className="text-sm text-gray-600">Total clôturés</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {Math.round(dossiers.reduce((sum, d) => sum + (d.progressGlobal || 0), 0) / dossiers.length)}%
              </p>
              <p className="text-sm text-gray-600">Progression moyenne</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {dossiers.filter(d => (d.progressGlobal || 0) >= 100).length}
              </p>
              <p className="text-sm text-gray-600">Complétés à 100%</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}